import { alpha, createTheme, darken, lighten } from '@mui/material';

const themeColors = {
  primary: '#e53e3e', // Vermelho principal da logo
  primaryLight: '#fc5a5a',
  primaryDark: '#c53030',
  secondary: '#2d3748', // Cinza escuro
  secondaryLight: '#4a5568',
  secondaryDark: '#1a202c',
  accent: '#ff6b35', // Laranja/vermelho vibrante
  background: '#f7fafc',
  surface: '#ffffff',
  text: '#2d3748',
  textSecondary: '#718096'
};

const ICVNTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: themeColors.primary,
      light: themeColors.primaryLight,
      dark: themeColors.primaryDark,
      contrastText: '#ffffff'
    },
    secondary: {
      main: themeColors.secondary,
      light: themeColors.secondaryLight,
      dark: themeColors.secondaryDark,
      contrastText: '#ffffff'
    },
    background: {
      default: themeColors.background,
      paper: themeColors.surface
    },
    text: {
      primary: themeColors.text,
      secondary: themeColors.textSecondary
    },
    error: {
      main: '#e53e3e',
      light: '#feb2b2',
      dark: '#c53030'
    },
    warning: {
      main: '#ed8936',
      light: '#fbd38d',
      dark: '#c05621'
    },
    info: {
      main: '#3182ce',
      light: '#90cdf4',
      dark: '#2c5282'
    },
    success: {
      main: '#38a169',
      light: '#9ae6b4',
      dark: '#2f855a'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      color: themeColors.text
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: themeColors.text
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: themeColors.text
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: themeColors.text
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: themeColors.text
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: themeColors.text
    },
    body1: {
      fontSize: '1rem',
      color: themeColors.text
    },
    body2: {
      fontSize: '0.875rem',
      color: themeColors.textSecondary
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          borderRadius: '12px',
          border: '1px solid rgba(226, 232, 240, 0.8)'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px',
          padding: '10px 24px'
        },
        contained: {
          boxShadow: '0 4px 6px -1px rgba(229, 62, 62, 0.3)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgba(229, 62, 62, 0.4)'
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: '6px'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(themeColors.secondary, 0.05),
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: themeColors.secondary,
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            letterSpacing: '0.05em'
          }
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(themeColors.primary, 0.04)
          }
        }
      }
    }
  },
  colors: {
    gradients: {
      blue1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      blue2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      blue3: 'linear-gradient(127deg, #667eea 0%, #764ba2 100%)',
      blue4: 'linear-gradient(135deg, #43cbff 0%, #9708cc 100%)',
      blue5: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      orange1: `linear-gradient(135deg, ${themeColors.accent} 0%, ${themeColors.primary} 100%)`,
      orange2: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      orange3: 'linear-gradient(120deg, #f6d365 0%, #fda085 100%)',
      purple1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      purple3: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      pink1: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      pink2: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      green1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      green2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      black1: `linear-gradient(100.66deg, ${themeColors.secondary} 6.56%, ${themeColors.secondaryDark} 93.57%)`,
      black2: `linear-gradient(100.66deg, ${themeColors.secondaryDark} 6.56%, ${themeColors.secondary} 93.57%)`
    },
    shadows: {
      success: '0 4px 6px -1px rgba(56, 161, 105, 0.3)',
      error: '0 4px 6px -1px rgba(229, 62, 62, 0.3)',
      primary: '0 4px 6px -1px rgba(229, 62, 62, 0.3)',
      warning: '0 4px 6px -1px rgba(237, 137, 54, 0.3)',
      info: '0 4px 6px -1px rgba(49, 130, 206, 0.3)'
    },
    alpha: {
      white: {
        5: alpha('#ffffff', 0.05),
        10: alpha('#ffffff', 0.1),
        30: alpha('#ffffff', 0.3),
        50: alpha('#ffffff', 0.5),
        70: alpha('#ffffff', 0.7),
        100: '#ffffff'
      },
      trueWhite: {
        5: alpha('#ffffff', 0.05),
        10: alpha('#ffffff', 0.1),
        30: alpha('#ffffff', 0.3),
        50: alpha('#ffffff', 0.5),
        70: alpha('#ffffff', 0.7),
        100: '#ffffff'
      },
      black: {
        5: alpha(themeColors.secondary, 0.05),
        10: alpha(themeColors.secondary, 0.1),
        30: alpha(themeColors.secondary, 0.3),
        50: alpha(themeColors.secondary, 0.5),
        70: alpha(themeColors.secondary, 0.7),
        100: themeColors.secondary
      }
    },
    secondary: {
      lighter: lighten(themeColors.secondary, 0.8),
      light: lighten(themeColors.secondary, 0.3),
      main: themeColors.secondary,
      dark: darken(themeColors.secondary, 0.2)
    },
    primary: {
      lighter: lighten(themeColors.primary, 0.8),
      light: lighten(themeColors.primary, 0.3),
      main: themeColors.primary,
      dark: darken(themeColors.primary, 0.2)
    },
    success: {
      lighter: '#c6f6d5',
      light: '#9ae6b4',
      main: '#38a169',
      dark: '#2f855a'
    },
    warning: {
      lighter: '#fef5e7',
      light: '#fbd38d',
      main: '#ed8936',
      dark: '#c05621'
    },
    error: {
      lighter: '#fed7d7',
      light: '#feb2b2',
      main: '#e53e3e',
      dark: '#c53030'
    },
    info: {
      lighter: '#bee3f8',
      light: '#90cdf4',
      main: '#3182ce',
      dark: '#2c5282'
    }
  },
  general: {
    reactFrameworkColor: themeColors.primary,
    borderRadiusSm: '6px',
    borderRadius: '8px',
    borderRadiusLg: '12px',
    borderRadiusXl: '16px'
  },
  sidebar: {
    background: themeColors.surface,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    width: '280px',
    textColor: themeColors.text,
    dividerBg: alpha(themeColors.secondary, 0.1),
    menuItemColor: themeColors.textSecondary,
    menuItemColorActive: themeColors.primary,
    menuItemBg: 'transparent',
    menuItemBgActive: alpha(themeColors.primary, 0.08),
    menuItemIconColor: themeColors.textSecondary,
    menuItemIconColorActive: themeColors.primary,
    menuItemHeadingColor: themeColors.secondary
  },
  header: {
    height: '72px',
    background: themeColors.surface,
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    textColor: themeColors.text
  }
});

export { ICVNTheme };
